import { useEffect, useRef, useState } from "react";
import { createChart, CrosshairMode } from "lightweight-charts";
import { Loader2 } from "lucide-react";
import { CandlesBuild, GooeyConnect } from "./motion";
import { api, errMsg } from "../lib/api";
import { useTick } from "../lib/socket";
import { sma, ema, vwap, bollinger, rsi, macd, heikinAshi } from "../lib/indicators";

const TFS = ["1m", "3m", "5m", "15m", "30m", "1h", "1D", "1W", "1M"];
const TF_SEC = { "1m": 60, "3m": 180, "5m": 300, "15m": 900, "30m": 1800, "1h": 3600, "1D": 86400, "1W": 604800, "1M": 2592000 };
const INDS = ["SMA", "EMA", "VWAP", "BB", "RSI", "MACD"];
const UP = "#1FBF8F";
const DOWN = "#F0505A";

function themeOpts() {
  const dark = document.documentElement.classList.contains("dark");
  return {
    layout: { background: { color: "transparent" }, textColor: dark ? "#8C8577" : "#5B5547", fontFamily: "JetBrains Mono", fontSize: 11 },
    grid: { vertLines: { color: dark ? "rgba(212,175,55,0.045)" : "rgba(120,100,60,0.08)" }, horzLines: { color: dark ? "rgba(212,175,55,0.045)" : "rgba(120,100,60,0.08)" } },
    rightPriceScale: { borderColor: dark ? "#221F19" : "#E5DFD2" },
    timeScale: { borderColor: dark ? "#221F19" : "#E5DFD2", timeVisible: true, secondsVisible: false },
    crosshair: { mode: CrosshairMode.Normal, vertLine: { color: "rgba(212,175,55,0.45)", labelBackgroundColor: "#B8912F" }, horzLine: { color: "rgba(212,175,55,0.45)", labelBackgroundColor: "#B8912F" } },
    localization: { locale: "en-IN" },
  };
}

function Toolbar({ tf, setTf, type, setType, inds, toggle }) {
  const btn = (active) => `h-7 px-2 rounded text-[11px] font-medium transition-colors duration-150 ${active ? "bg-primary/15 text-primary" : "text-muted-foreground hover:bg-accent hover:text-foreground"}`;
  return (
    <div className="flex flex-wrap items-center gap-1 px-2 py-1.5 border-b border-border">
      {TFS.map((t) => <button key={t} data-testid={`chart-tf-${t}`} className={btn(tf === t)} onClick={() => setTf(t)}>{t}</button>)}
      <span className="w-px h-4 bg-border mx-1" />
      {[["candles", "Candles"], ["line", "Line"], ["ha", "Heikin Ashi"]].map(([v, l]) => (
        <button key={v} data-testid={`chart-type-${v}`} className={btn(type === v)} onClick={() => setType(v)}>{l}</button>
      ))}
      <span className="w-px h-4 bg-border mx-1" />
      {INDS.map((i) => <button key={i} data-testid={`chart-ind-${i}`} className={btn(inds.includes(i))} onClick={() => toggle(i)}>{i}</button>)}
    </div>
  );
}

export function PriceChart({ instrumentKey }) {
  const box = useRef(null);
  const osc = useRef(null);
  const refs = useRef({});
  const [tf, setTf] = useState("5m");
  const [type, setType] = useState("candles");
  const [inds, setInds] = useState(["VWAP"]);
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const tick = useTick(instrumentKey);
  const toggle = (i) => setInds((x) => (x.includes(i) ? x.filter((y) => y !== i) : [...x, i]));
  const hasOsc = inds.includes("RSI") || inds.includes("MACD");

  useEffect(() => {
    let live = true;
    setData(null); setErr(null);
    api.get("/candles", { params: { key: instrumentKey, tf } })
      .then((r) => { if (live) setData(r.data.candles); })
      .catch((e) => { if (live) setErr(errMsg(e)); });
    return () => { live = false; };
  }, [instrumentKey, tf]);

  useEffect(() => {
    if (!data || !box.current) return undefined;
    const chart = createChart(box.current, { ...themeOpts(), autoSize: true });
    const bars = type === "ha" ? heikinAshi(data) : data;
    const main = type === "line"
      ? chart.addLineSeries({ color: "#D4AF37", lineWidth: 2 })
      : chart.addCandlestickSeries({ upColor: UP, downColor: DOWN, borderVisible: false, wickUpColor: UP, wickDownColor: DOWN });
    main.setData(bars.map((d) => (type === "line" ? { time: d.t, value: d.c } : { time: d.t, open: d.o, high: d.h, low: d.l, close: d.c })));
    const vol = chart.addHistogramSeries({ priceFormat: { type: "volume" }, priceScaleId: "vol" });
    chart.priceScale("vol").applyOptions({ scaleMargins: { top: 0.82, bottom: 0 } });
    vol.setData(data.map((d) => ({ time: d.t, value: d.v, color: d.c >= d.o ? "rgba(31,191,143,0.26)" : "rgba(240,80,90,0.26)" })));
    const line = (arr, color, w = 1) => { const s = chart.addLineSeries({ color, lineWidth: w, priceLineVisible: false, lastValueVisible: false }); s.setData(arr); };
    if (inds.includes("SMA")) line(sma(data, 20), "#D4AF37");
    if (inds.includes("EMA")) line(ema(data, 50), "#A855F7");
    if (inds.includes("VWAP")) line(vwap(data), "#B8C2CF");
    if (inds.includes("BB")) { const b = bollinger(data); line(b.up, "rgba(148,163,184,0.7)"); line(b.lo, "rgba(148,163,184,0.7)"); }
    chart.timeScale().fitContent();
    let oc = null;
    if (hasOsc && osc.current) {
      oc = createChart(osc.current, { ...themeOpts(), autoSize: true });
      if (inds.includes("RSI")) { const s = oc.addLineSeries({ color: "#F59E0B", lineWidth: 1 }); s.setData(rsi(data)); }
      if (inds.includes("MACD")) {
        const m = macd(data);
        oc.addHistogramSeries({ priceScaleId: "right" }).setData(m.hist);
        oc.addLineSeries({ color: "#E8C96A", lineWidth: 1 }).setData(m.macd);
        oc.addLineSeries({ color: "#F97316", lineWidth: 1 }).setData(m.signal);
      }
      oc.timeScale().fitContent();
      oc.applyOptions({ timeScale: { visible: true } });
      const sync = () => { const r = chart.timeScale().getVisibleRange(); if (r) { try { oc.timeScale().setVisibleRange(r); } catch (e) { /* range outside data */ } } };
      chart.timeScale().subscribeVisibleTimeRangeChange(sync);
      setTimeout(sync, 0);
    }
    refs.current = { main, vol, last: { ...bars[bars.length - 1] }, type };
    return () => { chart.remove(); if (oc) oc.remove(); refs.current = {}; };
  }, [data, type, inds, hasOsc]);

  useEffect(() => {
    const r = refs.current;
    if (!r.main || !tick?.ltp || !r.last) return;
    const sec = TF_SEC[tf];
    const now = Math.floor(Date.now() / 1000) + 19800;
    const bucket = sec < 86400 ? now - (now % sec) : r.last.t;
    const p = tick.ltp;
    if (bucket > r.last.t) r.last = { t: bucket, o: p, h: p, l: p, c: p, v: 0 };
    else r.last = { ...r.last, h: Math.max(r.last.h, p), l: Math.min(r.last.l, p), c: p };
    const b = r.last;
    r.main.update(r.type === "line" ? { time: b.t, value: b.c } : { time: b.t, open: b.o, high: b.h, low: b.l, close: b.c });
  }, [tick?.ltp, tf]);

  return (
    <div className="flex flex-col h-full" data-testid="price-chart">
      <Toolbar tf={tf} setTf={setTf} type={type} setType={setType} inds={inds} toggle={toggle} />
      <div className="relative flex-1 min-h-[280px]">
        {!data && !err && <CandlesBuild />}
        <GooeyConnect />
        {err && <div className="absolute inset-0 grid place-items-center text-[12px] text-down" data-testid="chart-error">{err}</div>}
        <div ref={box} className="absolute inset-0" />
      </div>
      {hasOsc && <div className="relative h-32 border-t border-border"><div ref={osc} className="absolute inset-0" /></div>}
    </div>
  );
}
